import { useState } from "react";
import { api, ApiError } from "../api/client";
import { useQuery } from "../api/useQuery";
import { useAuth } from "../auth/AuthContext";
import { fmtDateTime, fmtSize } from "../components/format";

interface Report {
  at: string;
  level: "ok" | "warning" | "critical";
  checks: { id: string; title: string; level: Report["level"]; detail: string }[];
}

interface BackupCopy {
  name: string;
  at: string;
  files: { name: string; size: number }[];
}

const LEVEL: Record<Report["level"], [string, string]> = {
  ok: ["ok", "в норме"],
  warning: ["warn", "внимание"],
  critical: ["danger", "сбой"],
};

/** Диагностика: состояние сервера, БД, очередей и резервных копий. */
export function StatusPage() {
  const { me } = useAuth();
  const { data, error, loading, reload } = useQuery<Report>("/api/v1/system/status");
  return (
    <>
      <div className="page-head">
        <div>
          <h2>Диагностика</h2>
          {data && <p className="muted small">Проверено {fmtDateTime(data.at)}</p>}
        </div>
        <button className="secondary" onClick={reload} disabled={loading}>Проверить снова</button>
      </div>
      {error && <div className="error" role="alert">{error}</div>}
      {data && (
        <div className="card" style={{ padding: 0 }}>
          <div className="list">
            {data.checks.map((c) => (
              <div key={c.id} className="row">
                <span>{c.title}</span>
                <span style={{ display: "flex", gap: 10, alignItems: "center", textAlign: "right" }}>
                  <span className="muted small">{c.detail}</span>
                  <span className={`badge ${LEVEL[c.level][0]}`}>{LEVEL[c.level][1]}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {me?.role === "admin" && <Backups />}

      <p className="muted small" style={{ marginTop: 14 }}>
        Сервер проверяет эти показатели каждые 5 минут и сообщает ответственным о переходе в «сбой»
        и о восстановлении — по почте или через webhook, если канал задан в настройках развёртывания.
      </p>
    </>
  );
}

/** Резервные копии на сервере: администратор скачивает копию на свой компьютер. */
function Backups() {
  const { data, error } = useQuery<{ items: BackupCopy[] }>("/api/v1/system/backups");
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [password, setPassword] = useState("");

  async function download(copy: string, file: string) {
    setBusy(`${copy}/${file}`);
    setFailed(null);
    try {
      await api.download(`/api/v1/system/backups/${copy}/${file}`, `${copy}-${file}`, { password });
      setPassword("");
    } catch (e) {
      setFailed(e instanceof ApiError ? e.message : "Не удалось скачать копию");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section style={{ marginTop: 22 }}>
      <h3>Резервные копии</h3>
      <p className="muted small">
        Копии хранятся на сервере; их нужно регулярно уносить за его пределы. Файл с расширением
        <code>.age</code> зашифрован ключом Заказчика — без закрытого ключа его не прочитать,
        незашифрованную копию храните как служебный документ: в ней все данные системы.
        Выдача подтверждается вашим паролем и записывается в журнал аудита.
      </p>
      {error && <div className="error" role="alert">{error}</div>}
      {failed && <div className="error" role="alert">{failed}</div>}
      {data && data.items.length === 0 && <p className="notice">Копий на сервере нет.</p>}
      {data && data.items.length > 0 && (
        <label className="field" style={{ maxWidth: 280, marginBottom: 12 }}>Ваш пароль
          <input type="password" value={password} autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)} />
        </label>
      )}
      {data && data.items.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div className="list">
            {data.items.map((c) => (
              <div key={c.name} className="row">
                <span>
                  {fmtDateTime(c.at)}
                  <span className="muted small" style={{ display: "block" }}>{c.name}</span>
                </span>
                <span style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {c.files.map((f) => (
                    <button key={f.name} className="secondary" disabled={busy !== null || !password}
                      onClick={() => void download(c.name, f.name)}>
                      {busy === `${c.name}/${f.name}` ? "Готовлю…" : `${f.name} · ${fmtSize(f.size)}`}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
